import { Context, Inject } from '@nuxt/types/app'

/**
 * Interface for interacting with local storage in a structured manner.
 *
 * Provides methods to get, set, reset, and purge data from named stores,
 * as well as to check if any data exists.
 *
 * @remarks
 * - The `store` parameter allows for logical separation of data within local storage.
 * - The `key` parameter identifies individual items within a store.
 *
 * @method get Retrieves the value associated with a given key in a store.
 * @param store The name of the store.
 * @param key The key within the store.
 * @returns The value associated with the key, or `undefined` if not found.
 *
 * @method set Stores a value under a given key in a store.
 * @param store The name of the store.
 * @param key The key within the store.
 * @param value The value to store.
 *
 * @method reset Removes all data from a specific store.
 * @param store The name of the store to reset.
 *
 * @method purge Removes all data from all stores.
 *
 * @method hasData Checks if any data exists in local storage.
 * @returns `true` if data exists, otherwise `false`.
 */
export interface LocalStorageInterface {
  get(store: string, key: string): unknown
  set(store: string, key: string, value: unknown): void
  reset(store: string): void
  purge(): void
  hasData(): boolean
}

declare module 'vue/types/vue' {
  interface Vue {
    $localStore: LocalStorageInterface
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $localStore: LocalStorageInterface
  }
  interface Context {
    $localStore: LocalStorageInterface
  }
}

declare module 'vuex/types/index' {
  // skipcq: JS-0387
  interface Store<S> {
    $localStore: LocalStorageInterface
  }
}

export function getStore(store: string): Record<string, string> {
  if (!localStorage.getItem(store)) {
    localStorage.setItem(store, '{}')
  }

  return JSON.parse(localStorage.getItem(store) as string)
}

export const localStorageApi: LocalStorageInterface = {
  get(store: string, key: string) {
    const storeObj = getStore(store)
    return storeObj[key]
  },
  set(store: string, key: string, value: string) {
    const storeObj = getStore(store)
    storeObj[key] = value
    localStorage.setItem(store, JSON.stringify(storeObj))
  },
  reset(store: string) {
    localStorage.setItem(store, '{}')
  },
  purge() {
    const cookieConsentState = localStorage.getItem('cookieConsent')
    localStorage.clear()
    if (cookieConsentState) localStorage.setItem('cookieConsent', cookieConsentState)
  },
  hasData() {
    if (localStorage.length === 0) return false

    const keys = Object.keys(localStorage)
    const nonRudderKeys = keys.filter((key) => !key.startsWith('rudder'))

    return nonRudderKeys.length > 0
  }
}

export default (_context: Context, inject: Inject): void => {
  inject('localStore', localStorageApi)
}
